import React, { useEffect, useState } from 'react';
import { BarChart3, Package, Scale, Users, CheckCircle2, AlertTriangle, TrendingUp, Globe, FileText, DollarSign } from 'lucide-react';
import api from '../api';

const MESES_LABEL = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const ORIGEN_LABEL = { usa: 'Miami', china: 'China' };
const ORIGEN_COLOR = { usa: '#3b82f6', china: '#ef4444' };

const ESTADO_COLORES = [
  '#3b82f6', '#8b5cf6', '#0ea5e9', '#22c55e', '#f59e0b',
  '#ef4444', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
  '#84cc16', '#06b6d4',
];

function KpiCard({ icon: Icon, label, valor, sub, color }) {
  return (
    <div style={{
      background: 'var(--color-bg)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-md)', padding: '16px 20px',
      display: 'flex', alignItems: 'center', gap: '14px',
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: '10px',
        background: (color || 'var(--color-primary)') + '18',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={20} style={{ color: color || 'var(--color-primary)' }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '22px', fontWeight: 700, lineHeight: 1.1 }}>{valor}</div>
        <div className="text-sm text-secondary">{label}</div>
        {sub && <div className="text-xs text-secondary">{sub}</div>}
      </div>
    </div>
  );
}

function MiniBarChart({ datos, labelKey, valueKey, color, prefix, formatValue }) {
  const max = Math.max(...datos.map(d => d[valueKey]), 1);
  const fmt = formatValue || ((v) => `${prefix || ''}${v}`);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '120px' }}>
      {datos.map((d, i) => {
        const h = max > 0 ? (d[valueKey] / max) * 90 : 0;
        const mesActual = new Date().getMonth();
        const opacity = i <= mesActual ? 1 : 0.3;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', opacity }}>
            <div className="text-xs" style={{ fontSize: '9px', color: 'var(--color-text-secondary)' }}>
              {d[valueKey] > 0 ? fmt(d[valueKey]) : ''}
            </div>
            <div style={{
              width: '100%', maxWidth: '28px', height: `${Math.max(h, 2)}px`,
              background: d[valueKey] > 0 ? (color || 'var(--color-primary)') : 'var(--color-bg-secondary)',
              borderRadius: '3px 3px 0 0',
            }} />
            <div className="text-xs" style={{ fontSize: '9px', color: 'var(--color-text-secondary)' }}>
              {MESES_LABEL[d[labelKey] - 1]}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function OrigenDesglose({ por_origen }) {
  const total = Object.values(por_origen).reduce((s, v) => s + v, 0) || 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {Object.entries(por_origen).map(([origen, cant]) => (
        <div key={origen} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '10px', height: '10px', borderRadius: '50%',
            background: ORIGEN_COLOR[origen] || '#9ca3af',
          }} />
          <span className="text-sm" style={{ flex: 1 }}>{ORIGEN_LABEL[origen] || origen}</span>
          <strong className="text-sm">{cant}</strong>
          <span className="text-xs text-secondary">{((cant / total) * 100).toFixed(0)}%</span>
        </div>
      ))}
      {Object.keys(por_origen).length === 0 && (
        <span className="text-sm text-secondary">Sin datos</span>
      )}
    </div>
  );
}

function EstadoDesglose({ por_estado }) {
  const total = por_estado.reduce((s, e) => s + e.c, 0) || 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {por_estado.map((e, i) => {
        const pct = ((e.c / total) * 100).toFixed(0);
        const color = ESTADO_COLORES[i % ESTADO_COLORES.length];
        return (
          <div key={e.estado_actual__nombre}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
              <span className="text-sm" style={{ flex: 1 }}>{e.estado_actual__nombre}</span>
              <strong className="text-sm" style={{ marginRight: '8px' }}>{e.c}</strong>
              <span className="text-xs text-secondary" style={{ width: '30px', textAlign: 'right' }}>{pct}%</span>
            </div>
            <div style={{ height: '4px', background: 'var(--color-bg-secondary)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '2px' }} />
            </div>
          </div>
        );
      })}
      {por_estado.length === 0 && (
        <span className="text-sm text-secondary">Sin datos</span>
      )}
    </div>
  );
}

export default function DashboardAdmin() {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    api.get('/admin/dashboard/')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setCargando(false));
  }, []);

  if (cargando) return <p className="text-secondary">Cargando dashboard...</p>;
  if (!data) return null;

  return (
    <div style={{ marginBottom: 'var(--spacing-6)' }}>
      <h2 style={{ marginBottom: 'var(--spacing-4)' }}>Dashboard {data.anio}</h2>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)',
      }}>
        <KpiCard icon={Package} label="Paquetes importados" valor={data.paquetes_importados} sub={`en ${data.anio}`} color="#8b5cf6" />
        <KpiCard icon={Scale} label="Libras manejadas" valor={`${data.libras_totales.toFixed(1)} lb`} color="#f59e0b" />
        <KpiCard icon={TrendingUp} label="Paquetes activos" valor={data.paquetes_activos} color="#0ea5e9" />
        <KpiCard icon={CheckCircle2} label="Entregados" valor={data.paquetes_entregados} sub={`${data.paquetes_entregados_anio} en ${data.anio}`} color="#22c55e" />
        <KpiCard icon={AlertTriangle} label="Retenidos / cancelados" valor={data.paquetes_retenidos_cancelados} color="#ef4444" />
        <KpiCard icon={Users} label="Clientes registrados" valor={data.clientes_registrados} color="#6366f1" />
        <KpiCard icon={DollarSign} label="Total facturado" valor={`$${data.total_facturado.toFixed(2)}`} sub={`en ${data.anio}`} color="#22c55e" />
        <KpiCard icon={FileText} label="Facturas SRI" valor={data.facturas_autorizadas} sub="autorizadas" color="#0ea5e9" />
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 'var(--spacing-3)',
      }}>
        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)', padding: '16px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <BarChart3 size={16} style={{ color: 'var(--color-text-secondary)' }} />
            <strong className="text-sm">Paquetes por mes</strong>
          </div>
          <MiniBarChart datos={data.paquetes_por_mes} labelKey="mes" valueKey="cantidad" color="#8b5cf6" />
        </div>

        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)', padding: '16px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <DollarSign size={16} style={{ color: 'var(--color-text-secondary)' }} />
            <strong className="text-sm">Facturado por mes</strong>
          </div>
          <MiniBarChart
            datos={data.facturado_por_mes} labelKey="mes" valueKey="monto" color="#22c55e" prefix="$"
            formatValue={(v) => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`}
          />
        </div>

        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)', padding: '16px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Globe size={16} style={{ color: 'var(--color-text-secondary)' }} />
            <strong className="text-sm">Origen de paquetes ({data.anio})</strong>
          </div>
          <OrigenDesglose por_origen={data.por_origen} />
        </div>

        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)', padding: '16px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Package size={16} style={{ color: 'var(--color-text-secondary)' }} />
            <strong className="text-sm">Paquetes por estado</strong>
          </div>
          <EstadoDesglose por_estado={data.por_estado} />
        </div>
      </div>
    </div>
  );
}
